import { FilterDropdown, type FilterOption } from '../FilterDropdown';
import { FilterSearchInput } from '../FilterSearchInput';
import { useEffect, useRef, useState } from 'react';
import './TableControls.css';

export interface TableControlsProps {
  onSearchChange: (value: string) => void;
  searchInput: string;
  selectedFilters: string[];
  onFiltersApply: (filters: string[]) => void;
  filterOptions?: FilterOption[];
  exclusiveFilterValues?: string[];
  className?: string;
  searchPlaceholder?: string;
  singleSelect?: boolean;
  expandCollapseLabel?: string;
  onExpandCollapse?: () => void;
  showExpandCollapse?: boolean;
  resultCount?: number;
  'data-testid'?: string;
}

function TableControls({
  onSearchChange,
  searchInput,
  selectedFilters,
  onFiltersApply,
  filterOptions,
  exclusiveFilterValues,
  className,
  searchPlaceholder = 'Search...',
  singleSelect = false,
  expandCollapseLabel,
  onExpandCollapse,
  showExpandCollapse,
  resultCount,
  'data-testid': dataTestId,
}: TableControlsProps) {
  const baseTestId = dataTestId ?? 'table-controls';
  const controlsClassName = className ? `access-table-controls ${className}` : 'access-table-controls';
  const filterKey = selectedFilters.join('|');
  const nextAnnouncementId = useRef(0);
  const [resultAnnouncement, setResultAnnouncement] = useState<{ id: number; text: string } | null>(null);

  useEffect(() => {
    if (resultCount === undefined) {
      setResultAnnouncement(null);

      return;
    }

    const searchTerm = searchInput.trim();
    const resultStatus =
      resultCount === 0
        ? `No results found${searchTerm ? ` for ${searchTerm}` : ''}.`
        : `${resultCount} ${resultCount === 1 ? 'result' : 'results'} found${searchTerm ? ` for ${searchTerm}` : ''}.`;
    const timeoutId = window.setTimeout(() => {
      nextAnnouncementId.current += 1;
      setResultAnnouncement({ id: nextAnnouncementId.current, text: resultStatus });
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [filterKey, resultCount, searchInput]);

  return (
    <div
      className={controlsClassName}
      data-testid={baseTestId}>
      <div
        aria-atomic='true'
        aria-live='polite'
        aria-relevant='additions text'
        className='ds-u-visibility--screen-reader'
        data-testid={`${baseTestId}-result-status`}
        role='status'>
        {resultAnnouncement && (
          <span
            key={resultAnnouncement.id}
            data-testid={`${baseTestId}-result-status-message`}>
            {resultAnnouncement.text}
          </span>
        )}
      </div>
      <div
        className='access-table-controls__search'
        data-testid={`${baseTestId}-search-container`}>
        <FilterSearchInput
          data-testid={`${baseTestId}-search`}
          label={searchPlaceholder}
          placeholder={searchPlaceholder}
          searchInput={searchInput}
          onSearchChange={onSearchChange}
        />
      </div>
      <div
        className='access-table-controls__actions'
        data-testid={`${baseTestId}-actions`}>
        {showExpandCollapse && expandCollapseLabel && onExpandCollapse && (
          <button
            className='access-table-controls__button'
            data-testid={`${baseTestId}-expand-all-button`}
            type='button'
            onClick={onExpandCollapse}>
            {expandCollapseLabel}
          </button>
        )}
        <FilterDropdown
          data-testid={`${baseTestId}-filter`}
          exclusiveFilterValues={exclusiveFilterValues}
          filterOptions={filterOptions}
          selectedFilters={selectedFilters}
          singleSelect={singleSelect}
          onFiltersApply={onFiltersApply}
        />
      </div>
    </div>
  );
}

export default TableControls;
